import React, { useMemo } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { ArrowLeft } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { useCurrencyFormatter } from '@shared/utils/currency';
import { minorToMajorUnits } from '@shared/utils/currency/minorUnits';
import type { PayoutAccountInfo, PayoutMethod } from './PayoutDialog.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { calcFeeInMinorUnits } from './payoutFeeUtils';
import { useMount } from '@shared/utils/hooks/useMount';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

interface MethodStepProps {
  readonly payoutAccountInfo: PayoutAccountInfo;
  readonly amountInMinorUnits: number;
  readonly selectedMethod: PayoutMethod;
  readonly onMethodChange: (method: PayoutMethod) => void;
  readonly onTransfer: () => void;
  readonly loadingTransfer: boolean;
  readonly onBack: () => void;
  readonly onPageViewed?: () => void;
}

export const MethodStep: React.FC<MethodStepProps> = ({
  payoutAccountInfo,
  amountInMinorUnits,
  selectedMethod,
  onMethodChange,
  onTransfer,
  loadingTransfer,
  onBack,
  onPageViewed,
}) => {
  const { tq } = useTranslation('cashFundsAdmin');
  const { formatMinorUnitsCurrency, formatCurrency } = useCurrencyFormatter();

  useMount(() => {
    onPageViewed?.();
  });

  const currencyCode = payoutAccountInfo.balance?.available.currencyCode ?? payoutAccountInfo.currencyCode ?? DEFAULT_CURRENCY;
  const externalAccount = payoutAccountInfo.defaultExternalAccount;
  const institutionDisplay = externalAccount ? `${externalAccount.institutionName ? `${externalAccount.institutionName} ` : ''}••••${externalAccount.last4}` : null;

  const availablePayoutMethods = externalAccount?.availablePayoutMethods ?? ['standard'];
  const [isInstantPayoutEnabled] = useIsFeatureEnabled('registryInstantPayoutEnabled');
  const hasInstant = isInstantPayoutEnabled && availablePayoutMethods.includes('instant') && payoutAccountInfo.instantPayoutFee != null;

  const selectedFee = selectedMethod === 'instant' ? payoutAccountInfo.instantPayoutFee : payoutAccountInfo.standardPayoutFee;

  const feeInMinorUnits = selectedFee ? calcFeeInMinorUnits(amountInMinorUnits, selectedFee) : 0;
  const netAmountInMinorUnits = amountInMinorUnits - feeInMinorUnits;

  const formattedNetAmount = formatMinorUnitsCurrency(netAmountInMinorUnits, currencyCode);

  const formatFeeLabel = (method: PayoutMethod): React.ReactNode => {
    const fee = method === 'instant' ? payoutAccountInfo.instantPayoutFee : payoutAccountInfo.standardPayoutFee;
    const isInstant = method === 'instant';
    const feeType = isInstant ? tq`Instant transfer fee` : tq`Bank transfer fee`;

    if (!fee || (fee.rate === 0 && fee.fixedValueInMinorUnits === 0)) {
      return tq`Free`;
    }

    const pct = fee.rate > 0 ? `${(fee.rate * 100).toFixed(2).replace(/\.?0+$/, '')}%` : null;
    const flat =
      fee.fixedValueInMinorUnits > 0
        ? formatCurrency({
            priceFloatingPointDecimalString: String(minorToMajorUnits(fee.fixedValueInMinorUnits, currencyCode)),
            priceCurrencyCode: currencyCode,
            formatForm: 'auto',
          })
        : null;

    const feeDesc = pct && flat ? `${pct} + ${flat}` : (pct ?? flat ?? '');
    return (
      <>
        {feeDesc} {feeType}
      </>
    );
  };

  const instantLabel = tq`Instant`;
  const standardLabel = tq`3-5 Business Days`;

  const methodOptions: Array<{ method: PayoutMethod; label: string; feeLabel: React.ReactNode }> = useMemo(() => {
    const options: Array<{ method: PayoutMethod; label: string; feeLabel: React.ReactNode }> = [];
    if (hasInstant) {
      options.push({
        method: 'instant',
        label: instantLabel,
        feeLabel: formatFeeLabel('instant'),
      });
    }
    // Standard is always offered — even if not in availablePayoutMethods, Stripe accepts it as a fallback
    options.push({
      method: 'standard',
      label: standardLabel,
      feeLabel: formatFeeLabel('standard'),
    });
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasInstant, payoutAccountInfo.instantPayoutFee, payoutAccountInfo.standardPayoutFee, currencyCode, instantLabel, standardLabel]);

  return (
    <>
      <Flex
        alignItems="center"
        marginBottom={6}
      >
        <ButtonV2
          variant="ghost"
          intent="neutral"
          size="sm"
          onClick={onBack}
          paddingX={0}
        >
          <ArrowLeft />
        </ButtonV2>
        <Flex
          flex={1}
          justifyContent="center"
        >
          <TextV2 typographyVariant="display1">{tq`Transfer to Bank`}</TextV2>
        </Flex>
        <Box width="40px" />
      </Flex>

      <Flex
        justifyContent="center"
        marginBottom={6}
      >
        <TextV2
          typographyVariant="display1"
          color="mono14"
        >
          {formattedNetAmount}
        </TextV2>
      </Flex>

      <Flex
        flexDirection="column"
        gap={3}
        marginBottom={6}
      >
        {methodOptions.map(({ method, label, feeLabel }) => {
          const isSelected = selectedMethod === method;
          return (
            <Flex
              key={method}
              borderStyle="solid"
              borderWidth="2px"
              borderColor={isSelected ? 'mono14' : 'mono3'}
              borderRadius={3}
              paddingX={4}
              paddingY={5}
              alignItems="center"
              alignSelf="stretch"
              gap={3}
              cursor="pointer"
              onClick={() => onMethodChange(method)}
            >
              <Box flex={1}>
                <TextV2
                  typographyVariant="hed1"
                  color="mono14"
                >
                  {label}
                </TextV2>
                {institutionDisplay && (
                  <TextV2
                    typographyVariant="label2"
                    color="mono10"
                    lineHeight="150%"
                  >
                    {institutionDisplay}
                  </TextV2>
                )}
                {feeLabel && (
                  <TextV2
                    typographyVariant="label2"
                    color="mono10"
                    lineHeight="150%"
                  >
                    {feeLabel}
                  </TextV2>
                )}
              </Box>
              <Box
                width="20px"
                height="20px"
                borderRadius="50%"
                borderStyle="solid"
                borderWidth="2px"
                borderColor={isSelected ? 'mono14' : 'mono5'}
                display="flex"
                alignItems="center"
                justifyContent="center"
                backgroundColor={isSelected ? 'mono14' : 'transparent'}
                flexShrink={0}
              >
                {isSelected && (
                  <Box
                    width="8px"
                    height="8px"
                    borderRadius="50%"
                    backgroundColor="white"
                  />
                )}
              </Box>
            </Flex>
          );
        })}
      </Flex>

      <TextV2
        typographyVariant="label2"
        color="mono10"
        marginBottom={6}
      >
        {tq`Account security checks may require additional time to make funds available.`}
        {hasInstant && <> {tq`Instant transfers typically post immediately but can take up to 2 hours.`}</>}
      </TextV2>

      <ButtonV2
        onClick={() => onTransfer()}
        size="lg"
        intent="neutral"
        shape="rounded"
        fullWidth
        loading={loadingTransfer}
      >
        {tq`Transfer`}
      </ButtonV2>
    </>
  );
};
